// src/App.tsx
import { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { MatomoProvider, createInstance } from '@datapunt/matomo-tracker-react';
// NOTE: @sentry/react is NOT imported statically — it is ~95 kB and sat on the
// eager first-paint path. See the deferred init at the bottom of this file.
import Routes from './routes';
import './static/styles/global.css';
import { store } from './store';
import MuiTheme from './MuiTheme';
import { tokenRefreshService } from './utils/tokenRefreshService';
import { AuthProvider } from './contexts/AuthContext';
import { prefetchHeavyAssets } from './utils/prefetchHeavyAssets';
import OfflineOverlay from '@/pwa/ui/OfflineOverlay';

/**
 * App boot sequence:
 *
 * 1. Mount Redux + Router shell (no UI yet — splash from index.html shows).
 * 2. Run tokenRefreshService.initialize() with a HARD 3-second ceiling.
 *    Even if /token/reset hangs, app must render. PrivateRoute handles the
 *    unauth state correctly.
 * 3. Once authReady → AuthProvider mounts, Routes render, splash fades.
 * 4. After authReady + browser idle, prefetch heavy chunks (charting lib,
 *    chart-vendor) so the first chart-page visit is instant.
 *
 * Why 3 seconds, not "wait for whatever it takes":
 *   - Slow 3G + cold SW + hung server should not produce a permanent splash.
 *   - 3s is past the 95th percentile for a healthy /token/reset round-trip.
 *   - If init hasn't finished by then, render anyway; it'll keep going in
 *     the background and the next API call will benefit from the result.
 */
const INIT_TIMEOUT_MS = 3000;

// Gives useMatomo() an instance; without the provider its trackEvent is a
// silent no-op (sign-in/sign-up events stopped reaching Matomo when the
// 2026-08-22 release merge dropped it). The tracker itself is set up by the
// snippet in index.html: _paq is already filled by then, so this skips its
// own setup and only pushes events onto that queue.
const matomo = createInstance({
  urlBase: 'https://matomo.rabex.ir/',
  siteId: 1
});

function App() {
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const initPromise = tokenRefreshService.initialize();

    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            `[App] tokenRefreshService.initialize did not complete in ${INIT_TIMEOUT_MS}ms — rendering anyway`
          );
        }
        resolve();
      }, INIT_TIMEOUT_MS);
    });

    Promise.race([initPromise, timeoutPromise]).finally(() => {
      setAuthReady(true);
    });

    // Recover gracefully when the user comes back online — common on
    // mobile when the app was opened with no network.
    const onOnline = () => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[App] Network back online — checking session');
      }
      tokenRefreshService.checkAndRefreshIfNeeded().catch(() => {
        // Already logged inside the service
      });
    };
    window.addEventListener('online', onOnline);

    return () => {
      window.removeEventListener('online', onOnline);
      tokenRefreshService.stop();
    };
  }, []);

  // Heavy-asset prefetch — runs after first paint + browser idle.
  // This is what makes chart pages fast on first visit without slowing
  // initial load. The SW intercepts these requests and caches them.
  useEffect(() => {
    if (!authReady) return;
    const cancel = prefetchHeavyAssets();
    // eslint-disable-next-line consistent-return
    return cancel;
  }, [authReady]);

  return (
    <Provider store={store}>
      <BrowserRouter>
        <MuiTheme>
          {!authReady ? null : (
            // AuthProvider mounts AFTER initialize() completes.
            // Memory is guaranteed to be filled at this point.
            // AuthProvider's useState(() => authenticated()) returns
            // the correct value immediately — no async flicker.
            <AuthProvider>
              {/* @ts-expect-error matomo-tracker-react 0.5 types predate React 18 and omit `children` */}
              <MatomoProvider value={matomo}>
                <Routes />
              </MatomoProvider>
            </AuthProvider>
          )}
          <OfflineOverlay />
        </MuiTheme>
      </BrowserRouter>
    </Provider>
  );
}

// Sentry, loaded after first paint.
//
// This used to be a module-scope `Sentry.init()` fed by a static
// `import * as Sentry from '@sentry/react'`, which put sentry-vendor (~95 kB,
// ~30 kB gzip) on the eager entry path for every user on every page.
//
// TRADE-OFF: errors thrown between page load and this init resolving are NOT
// captured. That window is roughly one idle callback. If you need those early
// errors, the fix is an onerror/onunhandledrejection buffer that replays into
// Sentry once it loads — not going back to a static import.
const initSentry = () => {
  import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn: 'https://bf315dc69ce2f2f4bd05104e9f7ff1c7@sentry.rabex.co/4',
        integrations: [new Sentry.BrowserTracing()],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
        environment: import.meta.env.MODE,

        // matomo.js calls `registration.sync.register('matomoSync')` on every
        // `online` event without catching the result. Android WebViews (and
        // Huawei/MIUI browsers) expose `sync` but have Background Sync
        // disabled, so it rejects as an unhandled rejection. Harmless, and
        // not ours to fix.
        ignoreErrors: [/Background Sync is disabled/]
      });
    })
    .catch(() => {
      // Never let monitoring break the app.
    });
};

if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(initSentry, { timeout: 5000 });
  } else {
    window.setTimeout(initSentry, 2000);
  }
}

export default App;
